import React from "react";
import { Typography } from "@optimizely/axiom";
import { ConfirmationDialog } from "../../ConfirmationDialog/ConfirmationDialog";
import { CustomGroupAccessDetails } from "./CustomGroupAccessDetails";
import { CUSTOM_GROUP_ACCESS_ACTION } from "../../../../constants/constants";
import { UserGroup } from "../../../../domain/UserGroup";

const determineActionText = (action: string | undefined) => {
    switch (action) {
        case CUSTOM_GROUP_ACCESS_ACTION.ADD:
            return {
                callToActionText: CUSTOM_GROUP_ACCESS_ACTION.ADD,
                style: "highlight" as const,
                subtitleAction: "adding",
                title: "Adding Group Access"
            };
        case CUSTOM_GROUP_ACCESS_ACTION.REMOVE:
            return {
                callToActionText: CUSTOM_GROUP_ACCESS_ACTION.REMOVE,
                style: "danger" as const,
                subtitleAction: "removing",
                title: "Removing Group Access"
            };
        default:
            return {
                callToActionText: "Confirm Access",
                style: "highlight" as const,
                subtitleAction: "reviewing",
                title: "Reviewing Group Access"
            };
    }
};

type CustomGroupAccessConfirmationModalProps = {
    action: `${CUSTOM_GROUP_ACCESS_ACTION}` | undefined;
    errorMessage: string | null;
    /**
     * Blocks confirmation while CustomGroupAccessDetails is still resolving the group's product
     * access. Opt in only if `onSubmit` actually reads that access: until the lookup succeeds it
     * publishes an empty map, which the invite/bulk flows would misread as "grants no product
     * access" (EPTS-6429).
     *
     * Consumers that act on the group id alone — AccessManagementUserForm adds and removes via
     * addUserGroupUsers/deleteUserGroupUsers — must leave this off, or a failed lookup would strand
     * them with a permanently disabled CTA.
     */
    isConfirmDisabled?: boolean;
    isSaving: boolean;
    userGroup: UserGroup;
    onClose: () => void;
    onSubmit: () => Promise<void>;
};

export const CustomGroupAccessConfirmationModal = ({
    action,
    errorMessage,
    isConfirmDisabled = false,
    isSaving,
    userGroup,
    onClose,
    onSubmit
}: CustomGroupAccessConfirmationModalProps) => {
    const actionText = determineActionText(action);
    const { callToActionText, style, subtitleAction, title } = actionText;

    const subtitle = (
        <>
            <Typography className="weight--bold" type="body">
                {`You are ${subtitleAction} access from a group (${userGroup.name}).`}
            </Typography>
            <div className="push--top">
                Please click to expand the following product(s) and review the respective access details before
                proceeding.
            </div>
        </>
    );

    return (
        <ConfirmationDialog
            callToActionText={callToActionText}
            loading={isSaving}
            isConfirmDisabled={isConfirmDisabled}
            error={errorMessage}
            onCancel={onClose}
            onConfirmation={onSubmit}
            // eslint-disable-next-line
            style={style}
            subtitle={subtitle}
            title={title}
        >
            <CustomGroupAccessDetails userGroup={userGroup} />
        </ConfirmationDialog>
    );
};

CustomGroupAccessConfirmationModal.displayName = "CustomGroupAccessConfirmationModal";
